import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PromoPopup = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [settings, setSettings] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Fetch popup settings
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API}/api/promo/popup`);
        const data = await res.json();
        
        if (data && data.enabled) {
          setSettings(data);
          
          // Show popup after delay (default 3 minutes = 180 seconds)
          const delay = (data.delay_seconds || 180) * 1000;
          
          setTimeout(() => {
            if (!dismissed) {
              setIsAnimating(true);
              setTimeout(() => setShow(true), 50);
            }
          }, delay);
        }
      } catch (err) {
        console.error('Error fetching promo popup:', err);
      }
    };

    fetchSettings();
  }, [dismissed]);

  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setShow(false);
      setDismissed(true);
    }, 300);
  };

  const formatEventDate = (dateStr, timeStr) => {
    if (!dateStr) return null;
    
    try {
      const date = new Date(dateStr);
      const options = { weekday: 'short', month: 'short', day: 'numeric' };
      const formattedDate = date.toLocaleDateString('en-GB', options);
      
      if (timeStr) {
        return `${formattedDate} at ${timeStr}`;
      }
      return formattedDate;
    } catch {
      return dateStr;
    }
  };

  if (!show || !settings || dismissed) return null;

  return (
    <div 
      className={`fixed bottom-4 right-4 z-[100] max-w-sm w-full mx-4 sm:mx-0 transition-all duration-300 ${
        isAnimating 
          ? 'translate-y-0 opacity-100' 
          : 'translate-y-full opacity-0'
      }`}
    >
      <div className="bg-gradient-to-br from-gray-900 via-purple-950/50 to-gray-900 rounded-2xl border border-purple-500/30 shadow-2xl shadow-purple-500/20 overflow-hidden">
        {/* Close Button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-gray-400 hover:text-white hover:bg-black/70 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        
        {/* Flyer Image */}
        {settings.flyer_image_url && (
          <div className="relative aspect-[16/9] overflow-hidden">
            <img 
              src={settings.flyer_image_url.startsWith('http') ? settings.flyer_image_url : `${API}${settings.flyer_image_url}`}
              alt="Event Flyer"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
          </div>
        )}
        
        {/* Content */}
        <div className="p-4">
          {/* Badge */}
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1 bg-red-500/20 border border-red-500/30 rounded-full px-2 py-0.5">
              <Sparkles className="h-3 w-3 text-red-400" />
              <span className="text-xs font-medium text-red-400">Live Event</span>
            </div>
          </div>
          
          {/* Headline */}
          <h3 className="text-lg font-bold text-white mb-1">
            {settings.headline}
          </h3>
          
          {/* Description */}
          <p className="text-sm text-gray-400 mb-3 line-clamp-2">
            {settings.description}
          </p>
          
          {/* Event Date/Time */}
          {(settings.event_date || settings.event_time) && (
            <div className="flex items-center gap-3 mb-4 text-sm">
              {settings.event_date && (
                <div className="flex items-center gap-1.5 text-purple-400">
                  <Calendar className="h-4 w-4" />
                  <span>{formatEventDate(settings.event_date, settings.event_time)}</span>
                </div>
              )}
            </div>
          )}
          
          {/* CTA Button */}
          <Button
            onClick={() => {
              if (settings.button_link) {
                if (settings.button_link.startsWith('http')) {
                  window.open(settings.button_link, '_blank');
                } else {
                  window.location.href = settings.button_link;
                }
              }
            }}
            className="w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white font-semibold rounded-xl"
          >
            {settings.button_text || 'Get Tickets'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PromoPopup;
